/**
 * Notification preferences as a modal popover (LEV-594, LEV-642 / LEV-637).
 *
 * Backed by `notification.preferences.{list,setTopic}` — Courier is the
 * authoritative pref store. Renders one row per Courier topic with a single
 * `OPTED_IN` / `OPTED_OUT` toggle. Channel selection (email vs in-app) is
 * configured per-topic in the Courier dashboard, not here.
 */
"use client";

import { Bell, X } from "lucide-react";
import { toast } from "react-toastify";

import { api } from "@/lib/trpc/react";

export type NotificationSettingsModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

export function NotificationSettingsModal({
  open,
  onOpenChange,
}: NotificationSettingsModalProps) {
  const utils = api.useContext();

  const { data, isLoading } = api.notification.preferences.list.useQuery(undefined, {
    enabled: open,
  });

  const setTopic = api.notification.preferences.setTopic.useMutation({
    // Optimistically flip the topic in the cache so the toggle responds
    // instantly. Each row mutates its own entry, so concurrent toggles
    // don't fight each other.
    onMutate: async (vars) => {
      await utils.notification.preferences.list.cancel();
      const previous = utils.notification.preferences.list.getData();
      utils.notification.preferences.list.setData(undefined, (old) =>
        old
          ? {
              topics: old.topics.map((t) =>
                t.topicId === vars.topicId ? { ...t, status: vars.status } : t,
              ),
            }
          : old,
      );
      return { previous };
    },
    onError: (err, _vars, context) => {
      if (context?.previous) {
        utils.notification.preferences.list.setData(undefined, context.previous);
      }
      toast.error(err.message ?? "Couldn't save preferences");
    },
    onSettled: () => {
      void utils.notification.preferences.list.invalidate();
    },
  });
  const { mutate: setTopicMutate } = setTopic;

  if (!open) return null;

  const topics = data?.topics ?? [];

  const handleToggle = (topicId: string, currentlyOptedIn: boolean) => {
    setTopicMutate({
      topicId,
      status: currentlyOptedIn ? "OPTED_OUT" : "OPTED_IN",
    });
  };

  return (
    <div
      role="dialog"
      aria-label="Notification settings"
      aria-modal="true"
      className="fixed inset-0 z-modal flex items-center justify-center bg-black/40"
      onClick={(e) => {
        if (e.target === e.currentTarget) onOpenChange(false);
      }}
    >
      <div className="w-full max-w-lg rounded-xl bg-white p-5 shadow-2xl">
        <div className="mb-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Bell className="h-4 w-4 text-slate-500" aria-hidden />
            <h2 className="text-base font-semibold text-slate-900">Notification settings</h2>
          </div>
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            aria-label="Close"
            className="rounded p-1 text-slate-500 hover:bg-slate-100 hover:text-slate-900"
          >
            <X className="h-4 w-4" aria-hidden />
          </button>
        </div>

        <div className="overflow-hidden rounded-md border border-slate-200">
          {isLoading && (
            <div className="px-3 py-4 text-xs text-slate-400">Loading topics…</div>
          )}
          {!isLoading && topics.length === 0 && (
            <div className="px-3 py-4 text-xs text-slate-400">
              No subscription topics configured.
            </div>
          )}
          {topics.length > 0 && (
            <ul className="divide-y divide-slate-100 text-sm">
              {topics.map((topic) => {
                const optedIn =
                  topic.status === "OPTED_IN" || topic.status === "REQUIRED";
                const required = topic.status === "REQUIRED";
                return (
                  <li
                    key={topic.topicId}
                    className="flex items-center justify-between px-3 py-2.5"
                  >
                    <span className="text-slate-700">{topic.topicName}</span>
                    <ToggleSwitch
                      checked={optedIn}
                      disabled={required}
                      ariaLabel={topic.topicName}
                      onChange={() => handleToggle(topic.topicId, optedIn)}
                    />
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}

function ToggleSwitch({
  checked,
  disabled,
  ariaLabel,
  onChange,
}: {
  checked: boolean;
  disabled?: boolean;
  ariaLabel: string;
  onChange: (next: boolean) => void;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={ariaLabel}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
        checked ? "bg-emerald-500" : "bg-slate-200"
      } disabled:opacity-50`}
    >
      <span
        className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
          checked ? "translate-x-4" : "translate-x-0.5"
        }`}
      />
    </button>
  );
}
